import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Header from '../../components/Dashboard/Header/Header';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Footer from '../../components/Common/Footer/Footer';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../components/Common/Select/PaginatedSelect';
import Pagination from '../../components/Common/Pagination/Pagination';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';
import { 
    Search, 
    Plus,
    Calendar,
    SlidersHorizontal as Filters,
    X,
    MoreVertical,
    Eye,
    Pencil,
    Trash2,
    LayoutDashboard,
    ChevronDown,
    ChevronUp,
    Clock,
    CheckCircle2,
    AlertCircle,
    CalendarDays,
    XCircle
} from 'lucide-react';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import styles from './LeaveRequests.module.css';
import LeaveRequestDrawer from './LeaveRequestDrawer';

const MySwal = withReactContent(Swal);

const LeaveRequests = () => {
    const navigate = useNavigate();
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const mainContentRef = useRef(null);
    const [isSummaryOpen, setIsSummaryOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const [selectedRequest, setSelectedRequest] = useState(null);
    const { loading, showLoader, hideLoader } = useLoading();
    const [leaveRequests, setLeaveRequests] = useState([]);
    
    // Drawer state
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [editingRequest, setEditingRequest] = useState(null);

    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
    const isAdmin = storedUser.role?.toLowerCase() === 'admin';
    
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        total: 0,
        per_page: 10
    });

    const [summary, setSummary] = useState({
        total_requests: 0,
        approved: 0,
        pending: 0,
        rejected: 0
    });

    const [filters, setFilters] = useState({
        status: null,
        leave_type: null,
        from_date: '',
        to_date: ''
    });

    const [leaveTypes, setLeaveTypes] = useState([]);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchTerm);
            setPagination(prev => ({ ...prev, current_page: 1 }));
        }, 500);
        return () => clearTimeout(timer);
    }, [searchTerm]);

    const fetchLeaveRequests = async () => {
        try {
            const response = await api.get('/leave', {
                params: {
                    page: pagination.current_page,
                    per_page: pagination.per_page,
                    search: debouncedSearch,
                    status: filters.status?.value,
                    leave_type_id: filters.leave_type?.value,
                    from_date: filters.from_date,
                    to_date: filters.to_date
                }
            });
            
            if (response.data && response.data.data) {
                const { data, current_page, last_page, total, per_page } = response.data.data;
                setLeaveRequests(data || []);
                setPagination({
                    current_page,
                    last_page,
                    total,
                    per_page
                });
            }
        } catch (error) {
            console.error('Error fetching leave requests:', error);
            toast.error('Failed to fetch leave requests');
        }
    };

    const fetchLeaveTypes = async () => {
        // Handled by PaginatedSelect
    };

    const fetchLeaveSummary = async () => {
        try {
            const response = await api.get('/leave/summary');
            if (response.data && response.data.data) {
                setSummary(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching leave summary:', error);
        }
    };

    useEffect(() => {
        fetchLeaveTypes();
        fetchLeaveSummary();
    }, []);

    useEffect(() => {
        fetchLeaveRequests();
    }, [pagination.current_page, pagination.per_page, debouncedSearch, filters]);

    const handleNewRequest = () => {
        setEditingRequest(null);
        setIsDrawerOpen(true);
    };

    const handleEditRequest = (req) => {
        setEditingRequest(req);
        setIsDrawerOpen(true);
        setOpenMenuIndex(null);
    };

    const handleApprove = async (req) => {
        setOpenMenuIndex(null);
        MySwal.fire({
            html: `
                <div class="custom-icon-wrapper-swal" style="background-color: #dcfce7;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                </div>
                <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Approve Leave?</h2>
                <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
                    Are you sure you want to approve the leave request for <strong>${req.employee?.employee_name || 'N/A'}</strong>?
                </p>
            `,
            showCancelButton: true,
            confirmButtonText: 'Yes, Approve',
            cancelButtonText: 'No, Keep Pending',
            customClass: {
                popup: 'custom-swal-popup',
                confirmButton: 'custom-swal-confirm',
                cancelButton: 'custom-swal-cancel',
                actions: 'custom-swal-actions-row'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const user = JSON.parse(localStorage.getItem('user') || '{}');
                    await api.post('/leave/approve', {
                        leave_id: req.id,
                        status: 'approved',
                        approver_id: user.id
                    });
                    toast.success('Leave request approved successfully');
                    fetchLeaveRequests();
                    fetchLeaveSummary();
                } catch (error) {
                    console.error('Error approving leave request:', error);
                    toast.error(error.response?.data?.message || 'Failed to approve leave request');
                }
            }
        });
    };

    const handleReject = async (req) => {
        setOpenMenuIndex(null);
        MySwal.fire({
            html: `
                <div class="custom-icon-wrapper-swal" style="background-color: #fee2e2;">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                </div>
                <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Reject Leave?</h2>
                <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
                    Are you sure you want to reject the leave request for <strong>${req.employee?.employee_name || 'N/A'}</strong>?
                </p>
            `,
            showCancelButton: true,
            confirmButtonText: 'Yes, Reject',
            cancelButtonText: 'No, Keep Pending',
            customClass: {
                popup: 'custom-swal-popup',
                confirmButton: 'custom-swal-confirm',
                cancelButton: 'custom-swal-cancel',
                actions: 'custom-swal-actions-row'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const user = JSON.parse(localStorage.getItem('user') || '{}');
                    await api.post('/leave/reject', {
                        leave_id: req.id,
                        approver_id: user.id
                    });
                    toast.success('Leave request rejected successfully');
                    fetchLeaveRequests();
                    fetchLeaveSummary();
                } catch (error) {
                    console.error('Error rejecting leave request:', error);
                    toast.error(error.response?.data?.message || 'Failed to reject leave request');
                }
            }
        });
    };

    const handleDelete = async (req) => {
        setOpenMenuIndex(null);
        
        MySwal.fire({
            html: `
                <div class="custom-icon-wrapper-swal">
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
                        <line x1="12" y1="9" x2="12" y2="13"></line>
                        <line x1="12" y1="17" x2="12.01" y2="17"></line>
                    </svg>
                </div>
                <h2 style="font-size: 22px; font-weight: 800; color: #0f172a; margin: 0 0 12px 0;">Delete Leave Request?</h2>
                <p style="margin: 0 0 8px 0; color: #475569; font-size: 15px; line-height: 1.5;">
                    Are you sure you want to delete this leave request for <strong>${req.employee?.employee_name || 'N/A'}</strong>?
                </p>
                <p style="margin: 0 0 0 0; color: #94a3b8; font-size: 13px;">
                    This action will permanently remove this record.
                </p>
            `,
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'No, Keep it',
            customClass: {
                popup: 'custom-swal-popup',
                confirmButton: 'custom-swal-confirm',
                cancelButton: 'custom-swal-cancel',
                actions: 'custom-swal-actions-row'
            },
            buttonsStyling: false
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.delete(`/leave/${req.id}`, {
                        data: { employee_id: JSON.parse(localStorage.getItem('user') || '{}').id }
                    });
                    toast.success('Leave request deleted successfully');
                    fetchLeaveRequests();
                } catch (error) {
                    console.error('Error deleting leave request:', error);
                    toast.error(error.response?.data?.message || 'Failed to delete leave request');
                }
            }
        });
    };

    const metrics = [
        {
            label: 'Total Requests',
            value: summary.total_requests.toString(),
            icon: <CalendarDays size={20} />,
            theme: 'blue'
        },
        {
            label: 'Approved',
            value: summary.approved.toString(),
            icon: <CheckCircle2 size={20} />,
            theme: 'green',
            highlight: true
        },
        {
            label: 'Pending',
            value: summary.pending.toString(),
            icon: <Clock size={20} />,
            theme: 'orange'
        },
        {
            label: 'Rejected',
            value: summary.rejected.toString(),
            icon: <AlertCircle size={20} />,
            theme: 'purple'
        }
    ];

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.action-wrapper')) {
                setOpenMenuIndex(null);
            }
        };
        const handleScroll = () => {
            if (openMenuIndex !== null) setOpenMenuIndex(null);
        };

        document.addEventListener('click', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [openMenuIndex]);

    const toggleActionMenu = (e, id, req) => {
        e.stopPropagation();
        if (openMenuIndex === id) {
            setOpenMenuIndex(null);
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            setMenuPosition({ top: rect.bottom + 4, left: rect.right - 160 });
            setOpenMenuIndex(id);
            setSelectedRequest(req);
        }
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
        if (mainContentRef.current) {
            mainContentRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const getStatusClass = (status) => {
        if (!status) return styles['status-pending'];
        const s = status.toLowerCase();
        switch (s) {
            case 'approved': return styles['status-completed'];
            case 'pending': return styles['status-pending'];
            case 'rejected': return styles['status-cancelled'];
            default: return styles['status-pending'];
        }
    };

    const getInitials = (name) => {
        if (!name) return '??';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const getDurationLabel = (duration) => {
        switch (duration) {
            case 'full_day': return 'Full Day';
            case 'first_half': return 'First Half';
            case 'second_half': return 'Second Half';
            default: return duration;
        }
    };

    const resetFilters = () => {
        setFilters({
            status: null,
            leave_type: null,
            from_date: '',
            to_date: ''
        });
        setSearchTerm('');
    };

    const hasActiveFilters = filters.status || filters.leave_type || filters.from_date || filters.to_date;

    return (
        <div className={styles['po-container']}>
            <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
            {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

            <div className={styles['content-wrapper']}>
                <Header onMenuClick={() => setIsSidebarOpen(true)} />

                <main className={styles['main-content']} ref={mainContentRef}>
                    <Breadcrumbs />

                    <div className={styles['page-header']}>
                        <div className={styles['header-title-section']}>
                            <h1>Manage Leave Request</h1>
                            <p>Track and manage employee leave applications across the organization.</p>
                        </div>
                        <button className={styles['add-btn']} onClick={handleNewRequest}>
                            <Plus size={20} />
                            <span>New Request</span>
                        </button>
                    </div>

                    <div className={styles['summary-collapsible']}>
                        <button
                            className={styles['summary-toggle']}
                            onClick={() => setIsSummaryOpen(prev => !prev)}
                        >
                            <div className={styles['summary-toggle-left']}>
                                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                                <div className={styles['summary-toggle-text']}>
                                    <span className={styles['summary-toggle-title']}>Summary</span>
                                    <span className={styles['summary-toggle-hint']}>
                                        {pagination.total} Total Requests
                                    </span>
                                </div>
                            </div>
                            {isSummaryOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </button>

                        <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
                            {metrics.map((m, i) => (
                                <div key={i} className={`${styles['metric-card']} ${styles[m.theme]} ${m.highlight ? styles['highlight-card'] : ''}`}>
                                    <div className={styles['metric-icon-box']}>
                                        {m.icon}
                                    </div>
                                    <div className={styles['metric-info']}>
                                        <span className={styles['metric-label']}>{m.label}</span>
                                        <div className={styles['metric-value-group']}>
                                            <span className={styles['metric-value']}>{m.value}</span>
                                        </div>
                                    </div>
                                    {m.highlight && <div className={styles['glow-effect']}></div>}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className={styles['table-controls']}>
                        <div className={styles['search-wrapper']}>
                            <Search className={styles['search-icon']} size={18} />
                            <input
                                type="text"
                                placeholder="Search by employee name..."
                                className={styles['search-input']}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                        <div className={styles['filter-actions']}>
                            <div className={styles['per-page-select-wrapper']}>
                                <CustomSelect
                                    isSearchable={false}
                                    backgroundColor="white"
                                    value={{ value: pagination.per_page, label: `${pagination.per_page} per page` }}
                                    options={[
                                        { value: 10, label: '10 per page' },
                                        { value: 25, label: '25 per page' },
                                        { value: 50, label: '50 per page' }
                                    ]}
                                    onChange={(opt) => setPagination(prev => ({ ...prev, per_page: opt.value, current_page: 1 }))}
                                />
                            </div>
                            <button 
                                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <Filters size={18} />
                                <span>Filters</span>
                                {hasActiveFilters && (
                                    <span className={styles['filter-badge']}></span>
                                )}
                            </button>
                        </div>
                    </div>

                    {showFilters && (
                        <div className={styles['filters-panel']}>
                            <div className={styles['filters-header']}>
                                <div className={styles['filters-title-group']}>
                                    <h3>Filter Requests</h3>
                                    <p>Drill down by status, leave type, or date range.</p>
                                </div>
                                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                                    <X size={18} />
                                </button>
                            </div>
                            <div className={styles['filters-grid']}>
                                <div className={styles['filter-group']}>
                                    <label>Status</label>
                                    <CustomSelect
                                        placeholder="All Status"
                                        options={[
                                            { value: 'pending', label: 'Pending' },
                                            { value: 'approved', label: 'Approved' },
                                            { value: 'rejected', label: 'Rejected' }
                                        ]}
                                        value={filters.status}
                                        onChange={(opt) => setFilters(prev => ({ ...prev, status: opt }))}
                                        isClearable
                                    />
                                </div>
                                <div className={styles['filter-group']}>
                                    <label>Leave Type</label>
                                    <PaginatedSelect
                                        endpoint="/leave-type/dropdown"
                                        valueKey="id"
                                        labelKey="name"
                                        value={filters.leave_type?.value}
                                        onChange={(val, opt) => setFilters(prev => ({ ...prev, leave_type: opt ? { value: opt.id, label: opt.name } : null }))}
                                        placeholder="All Types"
                                    />
                                </div>
                                <div className={styles['filter-group']}>
                                    <label>Start Date</label>
                                    <input 
                                        type="date" 
                                        className={styles['date-input']}
                                        value={filters.from_date}
                                        onChange={(e) => setFilters(prev => ({ ...prev, from_date: e.target.value }))}
                                    />
                                </div>
                                <div className={styles['filter-group']}>
                                    <label>End Date</label>
                                    <input 
                                        type="date" 
                                        className={styles['date-input']}
                                        value={filters.to_date}
                                        onChange={(e) => setFilters(prev => ({ ...prev, to_date: e.target.value }))}
                                    />
                                </div>
                            </div>
                            <div className={styles['filters-footer']}>
                                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
                            </div>
                        </div>
                    )}

                    <div className={[styles['custom-table-container'], 'card'].join(' ')}>
                        <div className={styles['custom-table-wrapper']}>
                            <table className={styles['custom-table']}>
                                <thead>
                                    <tr>
                                        <th>NO.</th>
                                        <th>EMPLOYEE NAME</th>
                                        <th>TYPE</th>
                                        <th>FROM DATE</th>
                                        <th>TO DATE</th>
                                        <th>DURATION</th>
                                        <th>REASON</th>
                                        <th>STATUS</th>
                                        <th className={styles['actions-cell']}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {leaveRequests.length > 0 ? (
                                        leaveRequests.map((req, index) => (
                                            <tr key={req.id}>
                                                <td className={styles['id-cell']}>
                                                    <span className={styles['receipt-text']}>{((pagination.current_page - 1) * pagination.per_page) + index + 1}</span>
                                                </td>
                                                <td>
                                                    <div className={styles['user-info']}>
                                                        <div 
                                                            className={styles['avatar']}
                                                            style={{ backgroundColor: '#e0f2fe', color: '#1e293b' }}
                                                        >
                                                            {getInitials(req.employee?.employee_name)}
                                                        </div>
                                                        <span 
                                                            className={`${styles['name-text']} ${styles['clickable']}`}
                                                            onClick={() => navigate(`/employees/leave-requests/view/${req.id}`)}
                                                        >
                                                            {req.employee?.employee_name || 'N/A'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>{req.leave_type?.name || 'N/A'}</td>
                                                <td className={styles['date-cell']}>{req.from_date}</td>
                                                <td className={styles['date-cell']}>{req.to_date}</td>
                                                <td>{getDurationLabel(req.leave_duration)}</td>
                                                <td>
                                                    <div className={styles['reason-cell']} title={req.reason}>
                                                        {req.reason || '-'}
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className={`${styles['status-badge']} ${getStatusClass(req.status)}`}>
                                                        {req.status?.toUpperCase()}
                                                    </span>
                                                </td>
                                                <td className={styles['actions-cell']}>
                                                    <div className={`action-wrapper ${styles['action-wrapper']}`}>
                                                        <button
                                                            className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                                                            onClick={(e) => toggleActionMenu(e, index, req)}
                                                        >
                                                            <MoreVertical size={18} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : !loading && (
                                        <NoData message="No leave requests found." isTable={true} />
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* Mobile View */}
                        <div className={styles['po-cards']}>
                            {leaveRequests.length > 0 ? (
                                leaveRequests.map((req, index) => (
                                    <div key={req.id} className={styles['po-card']}>
                                        <div className={styles['card-main']}>
                                            <div className={styles['card-info']}>
                                                <span className={styles['card-id']}>#{req.id}</span>
                                                <span className={styles['card-amount']}>{getDurationLabel(req.leave_duration)}</span>
                                            </div>
                                            <div className={`action-wrapper ${styles['action-wrapper']}`}>
                                                <button
                                                    className={styles['action-btn']}
                                                    onClick={(e) => toggleActionMenu(e, index, req)}
                                                >
                                                    <MoreVertical size={18} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className={styles['card-details']}>
                                            <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                                                <span className={styles['detail-label']}>Employee</span>
                                                <span className={styles['detail-value']}>{req.employee?.employee_name || 'N/A'}</span>
                                            </div>
                                            <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                                                <span className={styles['detail-label']}>Reason</span>
                                                <span className={styles['detail-value']}>{req.reason || '-'}</span>
                                            </div>
                                            <div className={styles['detail-item']}>
                                                <span className={styles['detail-label']}>Status</span>
                                                <span className={`${styles['status-badge']} ${getStatusClass(req.status)}`}>{req.status}</span>
                                            </div>
                                            <div className={styles['detail-item']}>
                                                <span className={styles['detail-label']}>Type</span>
                                                <span className={styles['detail-value']}>{req.leave_type?.name || 'N/A'}</span>
                                            </div>
                                        </div>
                                        <div className={styles['card-footer']}>
                                            <div className={styles['footer-left']}>
                                                <Calendar size={14} />
                                                <span>{req.from_date} - {req.to_date}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : !loading && (
                                <NoData message="No leave requests found." isTable={false} />
                            )}
                        </div>
                        
                        <div className={styles['desktop-pagination']}>
                            <Pagination 
                                currentPage={pagination.current_page}
                                totalItems={pagination.total}
                                itemsPerPage={pagination.per_page}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    </div>

                    <div className={styles['mobile-pagination']}>
                        <Pagination 
                            currentPage={pagination.current_page}
                            totalItems={pagination.total}
                            itemsPerPage={pagination.per_page}
                            onPageChange={handlePageChange}
                        />
                    </div>
                    
                    {/* Fixed Action Menu */}
                    {openMenuIndex !== null && (
                        <div
                            className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
                            style={{
                                position: 'fixed',
                                top: `${menuPosition.top}px`,
                                left: `${menuPosition.left}px`,
                                zIndex: 9999
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button onClick={() => {
                                navigate(`/employees/leave-requests/view/${selectedRequest.id}`);
                                setOpenMenuIndex(null);
                            }}>
                                <Eye size={16} /> View
                            </button>

                            {selectedRequest?.status === 'pending' && isAdmin && (
                                <>
                                    <button onClick={() => handleApprove(selectedRequest)} style={{ color: '#16a34a' }}>
                                        <CheckCircle2 size={16} /> Approve
                                    </button>
                                    <button onClick={() => handleReject(selectedRequest)} style={{ color: '#dc2626' }}>
                                        <XCircle size={16} /> Reject
                                    </button>
                                </>
                            )}

                            <button onClick={() => handleEditRequest(selectedRequest)}>
                                <Pencil size={16} /> Edit
                            </button>
                            <div className={styles['dropdown-divider']}></div>
                            <button 
                                className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                                onClick={() => handleDelete(selectedRequest)}
                            >
                                <Trash2 size={16} /> Delete
                            </button>
                        </div>
                    )}

                    <Footer />

                    <LeaveRequestDrawer 
                        isOpen={isDrawerOpen}
                        onClose={() => setIsDrawerOpen(false)}
                        onSave={() => {
                            fetchLeaveRequests();
                            setIsDrawerOpen(false);
                        }}
                        request={editingRequest}
                    />
                </main>
            </div>
        </div>
    );
};

export default LeaveRequests;
